import { useEffect, useState } from "react";
import { BookOpen, Plus, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";

export function SubjectsDialog({ open, onOpenChange }) {
  const [subjects, setSubjects] = useState([]);
  const [name, setName] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/subjects"); setSubjects(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load subjects"); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try { await api.post("/subjects", { name: name.trim() }); setName(""); load(); toast.success("Subject added"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };
  const remove = async (s) => {
    if (!confirm(`Delete subject "${s.name}"? Its teaching assignments will be removed too.`)) return;
    try { await api.delete(`/subjects/${s.id}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="subjects-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" /> Subjects</DialogTitle>
          <DialogDescription>Manage the subject list teachers can be assigned to (Math, Science, English…).</DialogDescription>
        </DialogHeader>
        <form onSubmit={add} className="flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Math" data-testid="subject-name-input" className={inputCls} />
          <button type="submit" data-testid="subject-add-btn" className="h-10 shrink-0 rounded-lg bg-primary px-4 text-sm font-medium text-white inline-flex items-center gap-1.5 hover:shadow transition">
            <Plus className="h-4 w-4" /> Add
          </button>
        </form>
        <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {subjects.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`subject-row-${s.id}`}>
              <span className="text-sm text-slate-900 inline-flex items-center gap-2">{s.name}{s.grades?.length > 0 && <span className="text-[11px] text-slate-500" data-testid={`subject-grades-${s.id}`}>Grade{s.grades.length === 1 ? "" : "s"} {s.grades.join(", ")}</span>}{s.district_id && <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700"><Building2 className="h-3 w-3" /> District</span>}</span>
              <button onClick={() => remove(s)} data-testid={`subject-delete-${s.id}`} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {subjects.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No subjects yet — add your first one above.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
